/* eslint-disable @next/next/no-img-element */
// RODAPÉ "Mega footer" (rodape = 4): 4 colunas no desktop (acordeão no mobile):
//  (a) logo + sobre curto + redes;
//  (b) "Imóveis por cidade" — links das top 8 cidades (allDataHome.cidades) →
//      /imoveis/<transacao>/<cidade-slug>/-/-/- (slug igual ao dos rodapés atuais);
//  (c) navegação (menus do site);
//  (d) contato completo (endereço, telefones com telN_nome, e-mail, CRECI/CNPJ).
// Fundos: cor3 (conteúdo) + faixa inferior cor6 (copyright). Contraste adaptativo REAL
// via ensureReadableTextColor (NÃO repete o bug hardcoded do rodapé 2).
// Default export = ErrorBoundary com fallback pro rodapé padrão (AboutPieces1).
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { FaFacebook, FaInstagram, FaLinkedin, FaMapMarkerAlt, FaTiktok, FaWhatsapp, FaYoutube } from 'react-icons/fa';
import { ensureReadableTextColor, isColorLight, MaskPhone, removerAcentos } from '../../../utils/FunctionsGlobals';
import { appendCodsiteQuery } from '../../../utils/url';
import { trackMetaEvent } from '../../../utils/metaPixel';
import { useAuth, useSiteRegiao } from '../../context/Info';
import { ehAtalhoDeRodape, siteUsaAtalhosRodape } from '../../../utils/footerShortcuts';
import Popular from '../populares/Popular';
import FooterShortcuts, { FooterShortcutItem } from './FooterShortcuts';
import { textosSecoesDoSite, tituloEmFrase } from '../../../utils/textosSecoesPorSite';
import { linkVerNoMapaRodape, mostraLinkEmpreendimentos } from '../../../utils/topoContato';
import styles from './MegaFooter.module.css';
import FiliaisRodape from '../filiais-rodape/FiliaisRodape';
import { SeloImobtotalItem } from '../selo-imobtotal/SeloImobtotal';

interface IMegaFooter {
    site: any;
    allCities?: any;
    origin?: any;
    codsite?: any;
    isMobile?: boolean;
    favorito?: any;
    allRodape?: any;
    hidePopular?: boolean;
    fallback?: React.ReactNode;
}

const citySlug = (label: string) => removerAcentos(String(label || '')).toLowerCase().replaceAll(' ', '-');

const MegaFooterInner: React.FC<IMegaFooter> = ({ site: siteProp, allCities, origin, codsite, allRodape, hidePopular }) => {
    const site = useSiteRegiao(siteProp);
    const { allDataHome } = useAuth();
    const router = useRouter();
    const codsiteQuery = Array.isArray(router.query?.codsite) ? router.query?.codsite[0] : router.query?.codsite;
    const [isMounted, setIsMounted] = useState(false);
    const [openCol, setOpenCol] = useState<number | null>(null);
    useEffect(() => setIsMounted(true), []);
    const withCodsite = (href: string) => (isMounted ? appendCodsiteQuery(href, codsite ?? codsiteQuery, origin) : href);
    // META PIXEL: helper de rastreio dos links de rodapé (ViewContent)
    const trackFooter = (nome: string, cat: string) => () =>
        trackMetaEvent(site, 'ViewContent', { content_name: nome, content_category: cat });

    // contraste adaptativo REAL sobre cada fundo
    const corConteudo = site?.cor3 || '#222222';
    const corText3 = ensureReadableTextColor(isColorLight(corConteudo) ? site?.cor11 : site?.cor13, corConteudo) || '#ffffff';
    const corHeader3 = ensureReadableTextColor(site?.cor2, corConteudo, 3.2) || corText3;
    const corBarra = site?.cor6 || '#1a1a1a';
    const corBarraText = ensureReadableTextColor(isColorLight(corBarra) ? site?.cor11 : site?.cor13, corBarra) || '#ffffff';

    const transacao = String(site?.transacoes?.[0] || 'venda').toLowerCase();
    const cidades = (allCities || []).filter((c: any) => c?.label).slice(0, 8);
    // ATALHOS INSTITUCIONAIS (cs2619): Trabalhe conosco / Política de privacidade / Quem
    // somos descem do menu pra faixa inferior do rodapé, em ÍCONE (clicar abre o conteúdo
    // em modal). Só valem no tenant do gate; fora dele a navegação segue idêntica.
    const menusDisponiveis = (allDataHome?.menus || []).filter((m: any) => m?.Disponivel && m?.titulo);
    const atalhos: FooterShortcutItem[] = siteUsaAtalhosRodape(site)
        ? menusDisponiveis
              .filter((m: any) => ehAtalhoDeRodape(site, m))
              .map((m: any) => ({ titulo: String(m.titulo), conteudo: m?.Conteudo ?? null }))
        : [];
    // o que virou ícone sai da lista de texto — senão o item apareceria duas vezes
    const menus = menusDisponiveis.filter((m: any) => !ehAtalhoDeRodape(site, m)).slice(0, 7);
    // O link fixo "/politica" (texto legal padrão da plataforma) só faz sentido quando o
    // tenant NÃO tem a própria página de privacidade — com ela virando ícone, some daqui.
    const mostrarPoliticaPadrao = !atalhos.some((a) => /privacidade|lgpd/i.test(a.titulo));

    const socials = [
        { href: site?.Dados?.instagram, Icon: FaInstagram, rede: 'Instagram' },
        { href: site?.Dados?.facebook, Icon: FaFacebook, rede: 'Facebook' },
        { href: site?.Dados?.youtube, Icon: FaYoutube, rede: 'Youtube' },
        { href: site?.Dados?.tiktok, Icon: FaTiktok, rede: 'Tiktok' },
        { href: site?.Dados?.Linkedin, Icon: FaLinkedin, rede: 'Linkedin' },
    ].filter((s) => s.href);

    const phones = [
        { tel: site?.Dados?.tel1, nome: site?.Dados?.tel1_nome, whats: site?.Dados?.tel1_whats },
        { tel: site?.Dados?.tel2, nome: site?.Dados?.tel2_nome, whats: site?.Dados?.tel2_whats },
        { tel: site?.Dados?.tel3, nome: site?.Dados?.tel3_nome, whats: site?.Dados?.tel3_whats },
        { tel: site?.Dados?.tel4, nome: site?.Dados?.tel4_nome, whats: site?.Dados?.tel4_whats },
    ].filter((p) => p.tel);
    const email = site?.Dados?.email1 || site?.Dados?.email2;
    const textosSite = textosSecoesDoSite(site?.id);
    const enderecoLinha = [site?.Dados?.endereco, [site?.Dados?.bairro, site?.Dados?.cidade, textosSite.rodapeEnderecoUf].filter(Boolean).join(' - ')]
        .filter(Boolean)
        .join(' - ');
    // "Ver no mapa" ao lado do endereço (utils/topoContato, cs2639); null nos demais sites
    const linkMapaEndereco = linkVerNoMapaRodape(site, enderecoLinha);
    const logoSrc = site?.url_logo ? `${site?.url_logo}?v=${site?.contador_marca || 0}` : '';

    // cs2639 (utils/textosSecoesPorSite): títulos das colunas em frase, sem a caixa alta do CSS.
    const colunasEmFrase = !!textosSite.titulosEmFrase;
    const columnHeader = (idx: number, label: string) => (
        <button
            type="button"
            className={colunasEmFrase ? `${styles.colHead} ${styles.semCaixaAlta}` : styles.colHead}
            style={{ color: corHeader3 }}
            aria-expanded={openCol === idx}
            onClick={() => setOpenCol(openCol === idx ? null : idx)}
        >
            {colunasEmFrase ? tituloEmFrase(label) : label}
            <span className={styles.chevron} aria-hidden="true">
                {openCol === idx ? '−' : '+'}
            </span>
        </button>
    );

    return (
        <>
        {/* As pesquisas populares vivem ACIMA do rodapé nos modelos 1/2/3; este
            rodapé já recebia as props e nunca renderizava o bloco — quem trocava
            pra cá perdia a seção (e os links de SEO) sem aviso nenhum. */}
        {!hidePopular && <Popular site={site} allRodape={allRodape} codsite={codsite} origin={origin}/>}
        {/* BUILDER: âncora estável do bloco "rodape". */}
        <footer className={styles.footer} data-builder-section="rodape">
            <div className={styles.content} style={{ background: corConteudo }}>
                <div className={styles.grid}>
                    {/* (a) marca */}
                    <div className={styles.col}>
                        {logoSrc ? (
                            <Link prefetch={false} href={withCodsite('/')} className={`${styles.logoLink} ${String(site?.id) === '2631' ? `${styles.logoLinkInteiraNoCelular} ${styles.logoLinkCabeNaColuna}` : ''}`} title={`Imobiliária ${site?.Dados?.empresa || ''}`}>
                                <Image src={logoSrc} alt={`Imobiliária ${site?.Dados?.empresa || ''}`} className={`${styles.logo} ${String(site?.id) === '2631' ? `${styles.logoInteiraNoCelular} ${styles.logoCabeNaColuna}` : ''}`} width={220} height={90} unoptimized />
                            </Link>
                        ) : (
                            <div className={styles.colHead} style={{ color: corHeader3 }}>
                                {site?.Dados?.empresa || ''}
                            </div>
                        )}
                        {textosSite.rodapeFrase || site?.frase_rodape || site?.Dados?.empresa ? (
                            <p className={styles.about} style={{ color: corText3, opacity: 0.85 }}>
                                {textosSite.rodapeFrase
                                    ? textosSite.rodapeFrase
                                    : site?.frase_rodape
                                    ? String(site.frase_rodape).replace(/<[^>]+>/g, ' ')
                                    : `${site?.Dados?.empresa} — imóveis para venda e locação${site?.Dados?.cidade ? ` em ${site?.Dados?.cidade} e região` : ''}.`}
                            </p>
                        ) : null}
                        {socials.length > 0 ? (
                            <div className={styles.socials}>
                                {socials.map((s, idx) => (
                                    <a
                                        key={idx}
                                        href={s.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={`Navegar para o ${s.rede} da imobiliária`}
                                        className={styles.social}
                                        style={{ color: corText3, borderColor: corText3 }}
                                    >
                                        <s.Icon aria-hidden="true" />
                                    </a>
                                ))}
                            </div>
                        ) : null}
                    </div>

                    {/* (b) cidades */}
                    {cidades.length > 0 ? (
                        <div className={styles.col}>
                            {columnHeader(1, 'Imóveis por cidade')}
                            <ul className={`${styles.list} ${openCol === 1 ? styles.listOpen : ''}`}>
                                {cidades.map((c: any) => (
                                    <li key={c?.value}>
                                        <Link
                                            prefetch={false}
                                            href={withCodsite(`/imoveis/${transacao}/${citySlug(c?.label)}/-/-/-`)}
                                            className={styles.link}
                                            style={{ color: corText3 }}
                                            title={`Imóveis em ${c?.label}`}
                                            onClick={trackFooter(c?.label, 'footer_city')}
                                        >
                                            {c?.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}

                    {/* (c) navegação */}
                    <div className={styles.col}>
                        {columnHeader(2, 'Navegação')}
                        <ul className={`${styles.list} ${openCol === 2 ? styles.listOpen : ''}`}>
                            {(site?.transacoes || []).map((t: string, idx: number) =>
                                t ? (
                                    <li key={`t-${idx}`}>
                                        <Link prefetch={false} href={withCodsite(`/imoveis/${String(t).toLowerCase()}/-/-/-/-`)} className={styles.link} style={{ color: corText3 }} onClick={trackFooter(`Imóveis para ${String(t).toLowerCase() === 'aluguel' ? 'alugar' : String(t).toLowerCase()}`, 'footer_institutional')}>
                                            Imóveis para {String(t).toLowerCase() === 'aluguel' ? 'alugar' : String(t).toLowerCase()}
                                        </Link>
                                    </li>
                                ) : null
                            )}
                            {/* "Empreendimentos" logo abaixo das transações — só quem pediu (utils/topoContato, cs2639) */}
                            {mostraLinkEmpreendimentos(site) ? (
                                <li>
                                    <Link prefetch={false} href={withCodsite('/condominios')} className={styles.link} style={{ color: corText3 }} onClick={trackFooter('Empreendimentos', 'footer_institutional')}>
                                        Empreendimentos
                                    </Link>
                                </li>
                            ) : null}
                            {menus.map((m: any, idx: number) => (
                                <li key={`m-${idx}`}>
                                    <Link prefetch={false} href={withCodsite(m?.link ? m.link : `/menu/${m.titulo}`)} className={styles.link} style={{ color: corText3 }} onClick={trackFooter(m.titulo, 'footer_institutional')}>
                                        {m.titulo}
                                    </Link>
                                </li>
                            ))}
                            {allDataHome?.blogs?.[0]?.id ? (
                                <li>
                                    <Link prefetch={false} href={withCodsite('/blog')} className={styles.link} style={{ color: corText3 }} onClick={trackFooter('Blog', 'footer_institutional')}>
                                        Blog
                                    </Link>
                                </li>
                            ) : null}
                            {mostrarPoliticaPadrao ? (
                                <li>
                                    <Link prefetch={false} href={withCodsite('/politica')} className={styles.link} style={{ color: corText3 }} onClick={trackFooter('Política de privacidade', 'footer_institutional')}>
                                        Política de privacidade
                                    </Link>
                                </li>
                            ) : null}
                        </ul>
                    </div>

                    {/* (d) contato */}
                    <div className={styles.col}>
                        {columnHeader(3, 'Contato')}
                        <div className={`${styles.contact} ${openCol === 3 ? styles.listOpen : ''}`}>
                            {enderecoLinha ? (
                                <p className={styles.contactLine} style={{ color: corText3 }}>
                                    {enderecoLinha}
                                    {linkMapaEndereco ? (
                                        <>
                                            {' '}
                                            <a className={styles.verNoMapa} href={linkMapaEndereco} target="_blank" rel="noopener noreferrer" style={{ color: corText3 }}>
                                                <FaMapMarkerAlt aria-hidden="true" />
                                                Ver no mapa
                                            </a>
                                        </>
                                    ) : null}
                                </p>
                            ) : null}
                            {phones.map((p, idx) => {
                                const href = p.whats
                                    ? `https://api.whatsapp.com/send?phone=55${(p.tel || '').replace(/\D/g, '')}`
                                    : `tel:+55${(p.tel || '').replace(/\D/g, '')}`;
                                return (
                                    <a key={idx} className={styles.contactLink} href={href} rel="noopener noreferrer" target={p.whats ? '_blank' : undefined} style={{ color: corText3 }}>
                                        {p.whats ? <FaWhatsapp aria-hidden="true" /> : null}
                                        {MaskPhone(p.tel)}
                                        {p.nome ? ` — ${p.nome}` : ''}
                                    </a>
                                );
                            })}
                            {email ? (
                                <a className={styles.contactLink} href={`mailto:${email}`} style={{ color: corText3 }}>
                                    {email}
                                </a>
                            ) : null}
                            {site?.Dados?.horario ? (
                                <p className={styles.contactLine} style={{ color: corText3, opacity: 0.85 }}>
                                    {site?.Dados?.horario}
                                </p>
                            ) : null}
                            {site?.Dados?.creci ? (
                                <p className={styles.contactMeta} style={{ color: corText3, opacity: 0.85 }}>
                                    CRECI {site?.Dados?.creci}
                                </p>
                            ) : null}
                            {site?.Dados?.cnpj ? (
                                <p className={styles.contactMeta} style={{ color: corText3, opacity: 0.85 }}>
                                    CNPJ {site?.Dados?.cnpj}
                                </p>
                            ) : null}
                        </div>
                    </div>
                </div>
                {/* MULTI-FILIAIS: endereços de TODAS as filiais (null fora do modo popup). */}
                <FiliaisRodape site={site} fundo={corConteudo} cor={corText3} />
            </div>

            <div className={styles.bottomBar} style={{ background: corBarra, color: corBarraText }}>
                <span>
                    © {new Date().getFullYear()} {site?.Dados?.empresa || ''}. Todos os direitos reservados.
                </span>
                {atalhos.length > 0 ? (
                    <FooterShortcuts
                        itens={atalhos}
                        site={site}
                        cor={corBarraText}
                        origin={origin}
                        codsite={codsite}
                        hrefDe={(titulo: string) => withCodsite(`/menu/${titulo}`)}
                    />
                ) : null}
                {/* Selo ImobTotal (28/09): linha própria na barra final, centralizado como o ©. */}
                <SeloImobtotalItem site={site} host={origin} alinhar="centro" linhaPropria fonte="13px" />
            </div>
        </footer>
        </>
    );
};

class MegaFooter extends React.Component<IMegaFooter, { hasError: boolean }> {
    constructor(props: IMegaFooter) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: degrada pro rodapé padrão sem quebrar a página.
    }
    render() {
        const { fallback, site } = this.props;
        if (this.state.hasError || !site) return fallback ?? null;
        return <MegaFooterInner {...this.props} />;
    }
}

export default MegaFooter;
